import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FYCgtResult } from './cgtCalculator';
import { formatDate } from './formatters';
import {
  DISCLAIMER,
  fyShortLabel,
  cleanNum,
  ratePct,
  holdPeriod,
} from './csvExport';

/**
 * Client-side PDF export of the Realised CGT report.
 *
 * Like the CSV export, this does NOT recompute or convert any CGT figure — it
 * prints what the engine produced (`FYCgtResult` + its `disposalsInFY`), already
 * in AUD at each leg's own date and already rounded to the cent. It reads the
 * same `Disposal` fields the CSV and the on-screen breakdown read, and shares
 * the CSV's formatting helpers, so all three mirror each other to the cent.
 */

const BRONZE: [number, number, number] = [184, 153, 104]; // #b89968 brand accent
const INK: [number, number, number] = [38, 38, 38];
const MUTED: [number, number, number] = [110, 110, 110];

// The per-disposal note (verbatim). The CSV carries the same wording with a
// "Note: " prefix; here it stands alone above the table.
const DISPOSAL_NOTE =
  "Per-disposal 'CGT owed' figures are shown individually and will not add up to the total Est. CGT owed above. The summary applies capital losses against gains before the 50% discount (ATO order of operations); the per-disposal rows do not.";

const PDF_DISPOSAL_HEADERS = [
  'Coin',
  'Symbol',
  'Qty sold',
  'Acquired',
  'Disposed',
  'Hold period',
  'Acq. fee (AUD)',
  'Proceeds (AUD)',
  'Disp. fee (AUD)',
  'Cost basis (AUD)',
  'Gross gain/loss (AUD)',
  'Discount?',
  'Discount amt (AUD)',
  'Taxable gain (AUD)',
  'CGT owed (AUD)',
];

/** ASCII-safe AUD amount, 2dp with thousands separators (mirrors the CSV's 2dp). */
function num2(n: number): string {
  if (!isFinite(n)) return '0.00';
  const sign = n < 0 ? '-' : '';
  return (
    sign +
    Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

/** "A$X,XXX.XX" for the summary figures (all non-negative). */
function aud(n: number): string {
  return `A$${num2(n)}`;
}

export interface CgtReportPdfParams {
  result: FYCgtResult;
  fy: { startYear: number; endYear: number };
  marginalRate: number;
  exportDate: Date;
}

export function cgtReportPdfFilename(fy: { startYear: number; endYear: number }): string {
  return `MyCryptoPlanner_CGT_Report_FY${fy.startYear}-${String(fy.endYear).slice(2)}.pdf`;
}

/** Build the CGT report PDF document. */
export function buildCgtReportPdf({
  result,
  fy,
  marginalRate,
  exportDate,
}: CgtReportPdfParams): jsPDF {
  // 15 columns → landscape so everything fits and wraps rather than overflows.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = 16;

  // ── Title (bronze accent) ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...BRONZE);
  doc.text('MyCryptoPlanner: Realised CGT Report', margin, y);
  y += 7;

  // ── FY + export date ──
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Financial year: ${fyShortLabel(fy)}`, margin, y);
  doc.text(`Export date: ${formatDate(exportDate.toISOString())}`, pageW - margin, y, {
    align: 'right',
  });
  y += 9;

  // ── Summary (small labelled table) ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text('Summary', margin, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: 'plain',
    styles: { fontSize: 9.5, cellPadding: 1.3 },
    columnStyles: {
      0: { cellWidth: 55, textColor: MUTED },
      1: { cellWidth: 45, halign: 'right', fontStyle: 'bold', textColor: INK },
    },
    body: [
      ['Net capital gain', aud(result.netCapitalGain)],
      ['CGT discount applied', aud(result.discountApplied)],
      ['Taxable gain', aud(result.taxableGain)],
      ['Marginal rate used', ratePct(marginalRate)],
      ['Est. CGT owed', aud(result.estCGT)],
    ],
    tableWidth: 'wrap',
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // ── Note (italic, wrapped) ──
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  const noteLines = doc.splitTextToSize(DISPOSAL_NOTE, pageW - margin * 2);
  doc.text(noteLines, margin, y);
  y += noteLines.length * 3.8 + 5;

  // ── Disposals ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text('Realised disposals this FY', margin, y);
  y += 2;

  if (result.disposalsInFY.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('No realised disposals this financial year.', margin, y + 6);
    y += 14;
  } else {
    // Newest sell first, then earliest parcel first — same order as the CSV / UI.
    const ordered = [...result.disposalsInFY].sort((a, b) => {
      if (a.disposalTs !== b.disposalTs) return b.disposalTs - a.disposalTs;
      return new Date(a.acquisitionDate).getTime() - new Date(b.acquisitionDate).getTime();
    });

    const body = ordered.map((d) => {
      return [
        d.name,
        d.symbol,
        cleanNum(d.quantity),
        formatDate(d.acquisitionDate),
        formatDate(d.disposalDate),
        holdPeriod(d.heldDays),
        num2(d.acquisitionFeeAud),
        num2(d.proceeds),
        num2(d.disposalFeeAud),
        num2(d.costBasis),
        num2(d.gain),
        d.discountEligible ? 'Yes' : 'No',
        num2(d.discountAmount),
        num2(d.taxableGain),
        num2(d.cgtOwed),
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin, bottom: 18 },
      head: [PDF_DISPOSAL_HEADERS],
      body,
      styles: {
        fontSize: 7,
        cellPadding: 1.4,
        overflow: 'linebreak',
        textColor: INK,
        lineColor: [225, 222, 215],
        lineWidth: 0.1,
      },
      headStyles: { fillColor: BRONZE, textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [249, 247, 243] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { halign: 'center', cellWidth: 12 },
        2: { halign: 'right' },
        5: { cellWidth: 16 },
        6: { halign: 'right' },   // acquisition fee
        7: { halign: 'right' },   // proceeds (net of the disposal fee)
        8: { halign: 'right' },   // disposal fee
        9: { halign: 'right' },   // cost basis (incl. the acquisition fee)
        10: { halign: 'right' },  // gross gain/loss
        11: { halign: 'center', cellWidth: 14 },
        12: { halign: 'right' },
        13: { halign: 'right' },
        14: { halign: 'right' },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ── Disclaimer (bordered box, italic) ──
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  const innerW = pageW - margin * 2 - 8;
  const discLines = doc.splitTextToSize(DISCLAIMER, innerW);
  const lineH = 4.2;
  const boxH = discLines.length * lineH + 9;
  if (y + boxH > pageH - 16) {
    doc.addPage();
    y = 16;
  }
  doc.setDrawColor(...BRONZE);
  doc.setLineWidth(0.3);
  doc.setFillColor(250, 248, 244);
  doc.roundedRect(margin, y, pageW - margin * 2, boxH, 1.5, 1.5, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...BRONZE);
  doc.text('DISCLAIMER', margin + 4, y + 5);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(discLines, margin + 4, y + 9.5);

  // ── Footer on every page ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by MyCryptoPlanner', margin, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  return doc;
}

/** Build and trigger download of the CGT report PDF. */
export function downloadCgtReportPdf(params: CgtReportPdfParams): void {
  const doc = buildCgtReportPdf(params);
  doc.save(cgtReportPdfFilename(params.fy));
}
